import { useState } from 'react';
import { HIRAGANA_SOURCE, API_URL } from '../utils/gameUtils';

export const useGame = (userId, fetchHighScores) => {
  const [gameState, setGameState] = useState('title');
  const [topicChar, setTopicChar] = useState('');
  const [score, setScore] = useState(0);
  const [history, setHistory] = useState([]);
  const [rejectedWords, setRejectedWords] = useState([]); // 却下された単語リスト (サーバー同期)
  const [applicationCandidates, setApplicationCandidates] = useState([]); // 申請候補（ゲーム中却下）
  const [loading, setLoading] = useState(false);

  // ゲーム開始
  const startGame = () => {
    const randomChar = HIRAGANA_SOURCE[Math.floor(Math.random() * HIRAGANA_SOURCE.length)];
    setTopicChar(randomChar);
    setScore(0);
    setHistory([]);
    setRejectedWords([]);
    setApplicationCandidates([]);
    setGameState('playing');
  };

  // リトライ
  const resetGame = () => {
    setGameState('title');
    if (userId) fetchHighScores(userId);
  };

  // ランキング画面へ
  const showRanking = () => {
    setGameState('ranking');
  };

  // 終了
  const finishGame = async (finalHistory, sessionRejected, pendingLevel3Words = []) => {
    // Calculate score with bonuses
    let finalScore = 0;
    finalHistory.forEach(word => {
      let points = 1;
      if (word.length >= 8) {
        points += 2;
      } else if (word.length >= 6) {
        points += 1;
      }
      finalScore += points;
    });
    setScore(finalScore);
    if (sessionRejected) {
      setApplicationCandidates(sessionRejected);
    }
    setGameState('result');

    // スコア送信
    if (userId) {
      try {
        setLoading(true);

        // Show verification toast (Managed by App.js or here? Managing DOM directly is not ideal in React hook but let's keep logic for now)
        // Ideally toast should be a component state. But for quick refactor, I will keep DOM manipulation or move it to state?
        // The original code appended to document.body. That's a side effect.
        // Let's copy specific toast logic or just remove toast logic?
        // User asked for refactoring, so improving code quality is good. 
        // I'll keep the toast logic for "parity" but it's ugly.
        // Or I will leave the toast logic to the component?
        // Let's include it here for now to preserve behavior exactly.

        const tempToast = document.createElement('div');
        tempToast.className = "fixed top-4 left-1/2 transform -translate-x-1/2 bg-blue-600 text-white px-4 py-2 rounded-full shadow-lg z-50 animate-bounce font-bold flex items-center gap-2";
        tempToast.innerHTML = '<svg class="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg> 最終判定中...';
        document.body.appendChild(tempToast);

        const res = await fetch(`${API_URL}/users/${userId}/scores`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            score: finalScore,
            words_history: finalHistory.join(','),
            topic: topicChar
          })
        });

        if (document.body.contains(tempToast)) {
          document.body.removeChild(tempToast);
        }

        if (res.ok) {
          const savedScore = await res.json();
          // Check if score was adjusted
          if (savedScore.score < finalScore) {
            const validWords = savedScore.words_history.split(',');
            const rejected = finalHistory.filter(w => !validWords.includes(w));
            setRejectedWords(rejected);
            setScore(savedScore.score); // Update display

            // 最終判定で除外された単語も申請可能にする
            const finalRejectedCandidates = rejected.map(w => ({
              word: w,
              reading: w, // 読み仮名は単語そのものをフォールバック
              reason: '最終判定で除外'
            }));
            setApplicationCandidates(prev => {
              const existing = new Set(prev.map(item => item.word));
              const newItems = finalRejectedCandidates.filter(item => !existing.has(item.word));
              return [...prev, ...newItems];
            });
          }
        }

        fetchHighScores(userId);
      } catch (e) {
        console.error("Failed to save score", e);
      } finally {
        setLoading(false);
      }
    }
  };

  return {
    gameState,
    topicChar,
    score,
    history,
    rejectedWords,
    applicationCandidates,
    loading,
    startGame,
    resetGame,
    showRanking,
    finishGame,
    setHistory,
    setGameState // Expose for routing
  };
};
